import {
  type EventParticipantRsvp,
  type EventStatus,
  type PrincipalCalendarCategory,
  type PrincipalCalendarEvent,
  type ProgramEventListType,
  type ProgramEventStatus,
  type ProgramListEvent,
  type ProgramUserRsvp,
  getCurrentUserRsvp,
} from 'models';
import { useCallback } from 'react';
import { type EventCalendarStatus, Icon } from 'ui';
import { useUser } from 'features/auth';
import type { ContentEvent } from '../calendar-layout-content';
import { useGetRsvpFromAttandee } from './useGetRsvpFromAttandee';
import { useMapPrincipalCalendarEventStatus } from './useMapPrincipalCalendarEventStatus';

const getRsvpStatus = (rsvp: EventParticipantRsvp | ProgramUserRsvp) => {
  switch (rsvp) {
    case 'going':
      return 'accepted';
    case 'not_going':
    case 'pending':
      return 'invited';
  }
};

const getEventSubtitle = (event: ProgramEventListType) => {
  switch (event) {
    case 'call':
    case 'clinical_review':
      return undefined;
    case 'conference':
    case 'video_conference':
      return 'Video Conference';
    case 'reminder':
      return 'Reminder';
    case 'telehealth_visit':
      return 'Telehealth Visit';
    case 'webinar':
      return 'Webinar';
  }
};

const getPrincipalCalendarEventSubtitle = (category: PrincipalCalendarCategory) => {
  switch (category) {
    case 'CALL':
      return 'Call';
    case 'CLINICAL_REVIEW':
      return 'Clinical Review';
    case 'REMINDER':
      return 'Reminder';
    case 'TELEHEALTH_VISIT':
      return 'Telehealth Visit';
    case 'VIDEO_CONFERENCE':
      return 'Video Conference';
    case 'WEBINAR':
      return 'Webinar';
  }
};

const mapEventStatusToEventCalendarStatus = (status: EventStatus | ProgramEventStatus): EventCalendarStatus => {
  switch (status) {
    case 'active':
    case 'draft':
    case 'published':
      return 'active';
    case 'canceled':
      return 'canceled';
  }
};

const getEventIcon = (category: PrincipalCalendarCategory) => {
  switch (category) {
    case 'CALL':
    case 'CLINICAL_REVIEW':
    case 'TELEHEALTH_VISIT':
    case 'VIDEO_CONFERENCE':
    case 'WEBINAR':
      return null;
    case 'REMINDER':
      return <Icon icon="bell-ringing-fill" size="extra-extra-small" />;
  }
};

export const useCreateCalendarEvents = () => {
  const {
    nodeUserData: { _id },
  } = useUser();

  const getRsvp = useGetRsvpFromAttandee();
  const mapPrincipalCalendarEventStatus = useMapPrincipalCalendarEventStatus();

  return useCallback(
    ({
      nodeEvents,
      railsEvents,
    }: {
      nodeEvents: PrincipalCalendarEvent[];
      railsEvents: ProgramListEvent[];
    }): ContentEvent[] => {
      const railsMapped: ContentEvent[] = railsEvents.map((event) => ({
        data: event,
        end: event.endTime,
        id: event.id,
        rsvp: getRsvpStatus(event.currentUserRsvp),
        start: event.startTime,
        status: mapEventStatusToEventCalendarStatus(event.status),
        subtitle: getEventSubtitle(event.eventType),
        title: event.name,
        type: 'rails',
      }));

      const nodeMapped: ContentEvent[] = nodeEvents
        .filter((event) => {
          const currentUserRsvp = getCurrentUserRsvp({
            participants: event.attendees?.map((attendee) => ({ ref: attendee.id, rsvp: getRsvp(attendee.partstat) })),
            viewerId: _id,
          });
          return event.organizer.id === _id || currentUserRsvp !== 'not_going';
        })
        .map((event) => {
          const currentUserRsvp = getCurrentUserRsvp({
            participants: event.attendees?.map((attendee) => ({ ref: attendee.id, rsvp: getRsvp(attendee.partstat) })),
            viewerId: _id,
          });
          const category = event.categories[0];

          return {
            data: event,
            end: event.endTime,
            icon: category ? getEventIcon(category) : undefined,
            id: event.id,
            isAllDay: event.isAllDay,
            rsvp: getRsvpStatus(currentUserRsvp ?? 'going'),
            start: event.startTime,
            status: mapPrincipalCalendarEventStatus(event.status),
            subtitle: category ? getPrincipalCalendarEventSubtitle(category) : undefined,
            title: event.summary,
            type: 'node',
          };
        });

      const sorted = [...railsMapped, ...nodeMapped].toSorted((a, b) => {
        const startDiff = a.start.getTime() - b.start.getTime();
        if (startDiff !== 0) {
          return startDiff;
        }

        return a.end.getTime() - b.end.getTime();
      });

      return sorted;
    },
    [_id, getRsvp, mapPrincipalCalendarEventStatus],
  );
};
